import { getMediaType } from './mediaUtils';

export interface ImageDimensions {
  width: number;
  height: number;
}

function normaliseMimeType(mimeType?: string | null) {
  return mimeType?.split(';')[0]?.trim().toLowerCase();
}

export function isVideoMimeType(mimeType?: string | null) {
  const normalisedMimeType = normaliseMimeType(mimeType);
  return (
    normalisedMimeType?.startsWith('video/') ||
    normalisedMimeType === 'application/mp4'
  );
}

const isVideoFile = (file: Blob, mimeType?: string | null) => {
  return isVideoMimeType(mimeType ?? file.type);
};

export const getVideoDimensions = (file: Blob) => {
  return new Promise<ImageDimensions>((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const dimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
      };
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video'));
    };

    video.src = objectUrl;
  });
};

const getImageDimensionsInternal = (file: Blob) => {
  return new Promise<ImageDimensions>((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const dimensions = {
        width: img.width,
        height: img.height,
      };
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

export const getImageOnlyDimensions = getImageDimensionsInternal;

export const getMediaDimensions = (file: Blob, mimeType?: string | null) => {
  if (isVideoFile(file, mimeType)) {
    return getVideoDimensions(file);
  }
  return getImageDimensionsInternal(file);
};

export const getImageDimensions = getMediaDimensions;

export const getRemoteMediaDimensions = (url: string) => {
  if (getMediaType(url) === 'video') {
    return new Promise<ImageDimensions>((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        reject(new Error('Failed to load remote video'));
      };
      video.src = url;
    });
  }

  return new Promise<ImageDimensions>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      reject(new Error('Failed to load remote image'));
    };
    image.src = url;
  });
};

export const formatDimensions = (dimensions: ImageDimensions | null) => {
  if (!dimensions) return 'Matching dimensions required';
  return `${dimensions.width}x${dimensions.height}px`;
};

export const dimensionsMatch = (
  dim1: ImageDimensions,
  dim2: ImageDimensions,
) => {
  return dim1.width === dim2.width && dim1.height === dim2.height;
};

export type MediaOrientation = 'portrait' | 'landscape' | 'square';

export const getOrientation = (
  dimensions: ImageDimensions,
): MediaOrientation => {
  if (dimensions.width > dimensions.height) return 'landscape';
  if (dimensions.height > dimensions.width) return 'portrait';
  return 'square';
};
